<template>
  <div class="product-spec-list">
    <!-- 単一グループ（33/38）は支給どおり `<p>` 1本だけ -->
    <p v-if="isFlat">
      <template v-for="(item, itemIndex) in groups[0].items" :key="itemIndex">
        【{{ item.label }}】<br />
        <template v-for="(line, lineIndex) in item.body" :key="lineIndex">
          {{ line }}<br />
        </template>
        <br v-if="itemIndex < groups[0].items.length - 1" />
      </template>
    </p>

    <!-- グループ入れ子（`K41`〜`K45` の5本のみ・各4グループ） -->
    <div v-for="(group, groupIndex) in isFlat ? [] : groups" :key="groupIndex" class="product-spec-list-item">
      <p>
        <template v-if="group.heading">
          <span class="w-strong">{{ group.heading }}</span><br />
        </template>
        <template v-for="(item, itemIndex) in group.items" :key="itemIndex">
          【{{ item.label }}】<br />
          <template v-for="(line, lineIndex) in item.body" :key="lineIndex">
            {{ line }}<br />
          </template>
          <br v-if="itemIndex < group.items.length - 1" />
        </template>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-spec-list`（成分・商品情報）。38/38。
 *
 * **支給は構造を持たない。** `<div>` の中に `<p>` が1本あるだけで、`【項目名】<br />値<br /><br />`
 * を並べた形（`design-src/20260810/product-detail/product501.html`）。それでも **const 側は
 * `{ label, body }` の構造で持つ**（決定#54）。支給8項目のうち【商品名】は API の `name`、
 * 【商品情報】は API の `topCatchphrase` ＋ `description` そのものなので、ベタなテキストで持つと
 * API と const の二重管理になるため。
 *
 * **項目数は 4〜9 の可変**（4項目4本＝`NS1` `NS2` `NS3` `NST` ／7項目3本 ／8項目22本 ／9項目6本）。
 * **`K41`〜`K45` の5本だけ `product-spec-list-item` が4グループ入れ子**になり、各グループに
 * `■KENCOS4について` のような小見出しが付く（1ページ22項目）。`<p>` 1本で済むのは 33/38。
 *
 * **値は既存コードが正・ラベルは支給デザインが正**（決定#61）。既存「一日の目安量」→
 * 支給【一日の摂取目安量】など。
 *
 * **決定#61 の例外は【アレルゲン（特定原材料に準ずるものも含む）】だけ**（決定#89・`C-17`）。
 * 18商品で支給の【アレルギー物質】を本番表記へ戻した。他のラベルは決定#61 のまま支給が正。
 *
 * **グループ小見出しの直後に改行を入れる**（`V-19`・決定#89）。支給の `.w-strong` は
 * `assets/css/renewal/style.css:91` で `font-weight` だけのインライン要素なので、支給どおりだと
 * `■KENCOS4について【使用の目安】` と同じ行に出てしまう。支給の不備なので実装側で改行する。
 *
 * 本文は行の配列（決定#56）。支給の当該ブロックに装飾インラインタグは無く、`<br>` だけ。
 * → CONVERSION-GUIDE.md §3.3.1 の #13 ／ 決定#54
 */
type SpecItem = {
  /** 項目名。表示時に `【】` で囲む */
  label: string;
  /** 値。1要素＝1行 */
  body: string[];
};

type SpecGroup = {
  /** `■KENCOS4について` のような小見出し。`K41`〜`K45` のみ */
  heading?: string;
  items: SpecItem[];
};

type Props = {
  groups: SpecGroup[];
};

const props = defineProps<Props>();

/** グループが1つで小見出しも無ければ、支給どおり `<p>` 1本で描く */
const isFlat = computed<boolean>(() => props.groups.length === 1 && !props.groups[0].heading);
</script>
